import { camelCase, lowerCase, startCase } from 'lodash-es';
import { getTzTimeNow, showErrorMsg, showSuccessMsg } from '@/common/utils/commonUtils';
import { i18n } from '@/common/locale';
import dayjs from 'dayjs';
import {
  LicenseTargetDisplayName,
  LicenseTargetLanguageKeys,
  LicenseTargetUnitLanguageKeys,
} from './license.data';
import { LicenseLevelType, LicenseType } from './license.type';

const LICENSE_POLICY_LABEL = {
  RESERVED: 'Reserved',
  FLEXIBLE: 'Flexible',
} as const;
const LICENSE_LEVEL_VALUES: readonly LicenseLevelType[] = [
  'PERM',
  'TERM',
  'PERM (Flexible)',
  'TERM (Flexible)',
  'TRIAL',
  'NONE',
] as const;

const normalizeLicenseLevel = (licenseLevel?: string | null): string => {
  if (!licenseLevel) {
    return '';
  }
  return licenseLevel.trim().toUpperCase();
};

const normalizeAssignmentPolicy = (
  assignmentPolicy?: string | null,
): keyof typeof LICENSE_POLICY_LABEL | '' => {
  if (!assignmentPolicy) {
    return '';
  }
  const normalized = assignmentPolicy.trim().toUpperCase();
  return normalized in LICENSE_POLICY_LABEL
    ? (normalized as keyof typeof LICENSE_POLICY_LABEL)
    : '';
};

const isLicenseLevelType = (value: string): value is LicenseLevelType => {
  return (LICENSE_LEVEL_VALUES as readonly string[]).includes(value);
};

/** 라이선스 타입 멀티셀렉트 옵션 집합이 동일한지(순서 무시) 비교 */
export const areLicenseTypeOptionSetsEqual = (a: string[], b: string[]): boolean =>
  a.length === b.length && [...a].sort().join('\u0001') === [...b].sort().join('\u0001');

/**
 * License Type 셀렉터 표시 순서: 알파벳 순(대소문자 구분 완화), `NONE`은 항상 맨 아래.
 */
export const sortLicenseTypeSelectorValues = (types: readonly string[]): string[] => {
  const NONE_LABEL = 'NONE';
  const rest = types.filter((t) => t !== NONE_LABEL);
  rest.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return types.includes(NONE_LABEL) ? [...rest, NONE_LABEL] : rest;
};

/**
 * API의 assignmentPolicy + licenseLevel(또는 licenseType) 조합으로 화면 표기용 License Type 문자열을 만든다.
 */
export const getComposedLicenseType = (
  licenseLevel?: string | null,
  assignmentPolicy?: string | null,
): LicenseLevelType | '' => {
  const normalizedLevel = normalizeLicenseLevel(licenseLevel);
  if (!normalizedLevel) {
    return '';
  }
  if (normalizedLevel === 'TRIAL' || normalizedLevel === 'NONE') {
    return normalizedLevel;
  }

  const compactLevel = normalizedLevel.replace(/[\s|]/g, '');
  const levelToken = compactLevel.includes('PERM')
    ? 'PERM'
    : compactLevel.includes('TERM')
      ? 'TERM'
      : '';
  const policyTokenFromLevel = compactLevel.includes('RESERVED')
    ? 'RESERVED'
    : compactLevel.includes('FLEXIBLE')
      ? 'FLEXIBLE'
      : '';

  if (levelToken && policyTokenFromLevel) {
    const composed = policyTokenFromLevel === 'FLEXIBLE' ? `${levelToken} (Flexible)` : levelToken;
    return isLicenseLevelType(composed) ? composed : '';
  }

  const policy = normalizeAssignmentPolicy(assignmentPolicy);
  if (levelToken && policy) {
    const composed = policy === 'FLEXIBLE' ? `${levelToken} (Flexible)` : levelToken;
    return isLicenseLevelType(composed) ? composed : '';
  }
  return isLicenseLevelType(normalizedLevel) ? normalizedLevel : '';
};

export const getDisplayLicenseTargetName = (licenseTarget: string): string => {
  return (
    LicenseTargetDisplayName[licenseTarget] ??
    (LicenseTargetLanguageKeys[licenseTarget]
      ? i18n.global.t(`WORD.${LicenseTargetLanguageKeys[licenseTarget]}`)
      : startCase(camelCase(licenseTarget)))
  );
};

export const getDisplayLicenseTargetUnit = (targetUnit: string): string => {
  const unit = lowerCase(targetUnit);
  return LicenseTargetUnitLanguageKeys[unit]
    ? i18n.global.t(`WORD.${LicenseTargetUnitLanguageKeys[unit]}`)
    : targetUnit;
};

export const getDisplayLicenseType = (targetType: string): string => {
  return i18n.global.t(`WORD.${targetType.toUpperCase()}`);
};

export const getDisplayComposedLicenseType = (
  licenseLevel?: string | null,
  assignmentPolicy?: string | null,
): string => {
  return getComposedLicenseType(licenseLevel, assignmentPolicy) || licenseLevel || '';
};

export const showLicenseNotification = (type: 'error' | 'success', ctx, message: string) => {
  if (type === 'error') {
    showErrorMsg(ctx, message);
  } else {
    showSuccessMsg(ctx, message);
  }
};

export const getDisplayLicenseEffectiveDate = (
  effectiveDate: string,
  licenseType: string,
): string => {
  const normalizedLicenseType = (licenseType ?? '').toUpperCase().replace(/[\s|]/g, '');
  const isReservedType = normalizedLicenseType === 'PERM' || normalizedLicenseType === 'TERM';
  if (!isReservedType) {
    return '-';
  }
  if (!dayjs(effectiveDate).isValid()) {
    return '-';
  }
  const gap = Math.ceil(dayjs(effectiveDate).diff(getTzTimeNow(), 'h') / 24);
  return gap > 0
    ? `D - ${gap}`
    : `${i18n.global.t('MESSAGE.UI.SETTING.LICENSE.LICENSE_ALLOCATED')}(${effectiveDate})`;
};

export const getLicenseTypeForRequest = (
  type: LicenseType,
): 'APPLICATION' | 'CLOUD' | 'CONTAINER' | 'DATABASE' | 'INFRASTRUCTURE' | undefined => {
  switch (type) {
    case 'application':
      return 'APPLICATION';
    case 'cloud':
      return 'CLOUD';
    case 'container':
      return 'CONTAINER';
    case 'database':
      return 'DATABASE';
    case 'infrastructure':
      return 'INFRASTRUCTURE';
    default:
      throw new Error('Invalid license type');
  }
};
